import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { CheckCircle, XCircle } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import Logo from '../components/Logo';

const API = process.env.REACT_APP_BACKEND_URL;

export default function LoginPage({ registerMode = false }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [inviteCode, setInviteCode] = useState('');
  const [inviteValid, setInviteValid] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [isRegister, setIsRegister] = useState(registerMode);
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Auto-pre-fill invite code + e-post from URL + show "session expired" toast
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const inviteFromUrl = params.get('invite');
    if (inviteFromUrl) {
      setInviteCode(inviteFromUrl);
      setIsRegister(true);
      validateInvite(inviteFromUrl);
    }
    if (params.get('reason') === 'expired') {
      toast.info('Sesjonen din utløp. Logg inn igjen for å fortsette.');
    }
  }, [location.search]);

  const validateInvite = async (code) => {
    try {
      const { data } = await axios.post(`${API}/api/marketing/invite/validate`, { code });
      setInviteValid(true);
      if (data.email) setEmail(data.email);
    } catch (err) {
      setInviteValid(false);
      setError(err.response?.data?.detail || 'Ugyldig invite-kode');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      if (isRegister) {
        await register(email, password, name, inviteCode);
        navigate('/onboarding');
      } else {
        await login(email, password);
        navigate('/');
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (typeof detail === 'string') setError(detail);
      else if (Array.isArray(detail)) setError(detail.map((e) => e.msg).join(' '));
      else setError(isRegister ? 'Registrering feilet' : 'Login feilet – sjekk detaljer');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4" data-testid="login-page">
      <div className="w-full max-w-sm">
        <Link to="/" className="flex items-center gap-2 mb-8 hover:opacity-80" data-testid="login-logo">
          <Logo variant="full" height={40} />
        </Link>

        <div className="bg-card border border-border rounded-md p-6">
          <h2 className="font-heading text-lg font-semibold mb-1">
            {isRegister ? 'Opprett konto' : 'Logg inn'}
          </h2>
          <p className="text-sm text-muted-foreground mb-6">
            {isRegister ? 'Velkommen! Fyll inn detaljer for å starte.' : 'Logg inn for å administrere din butikk'}
          </p>

          <form onSubmit={handleSubmit} className="space-y-4">
            {isRegister && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="invite">Invite-kode</Label>
                  <div className="relative">
                    <Input
                      id="invite"
                      type="text"
                      value={inviteCode}
                      onChange={(e) => setInviteCode(e.target.value)}
                      onBlur={() => inviteCode && validateInvite(inviteCode)}
                      placeholder="Fra e-posten din"
                      data-testid="invite-code-input"
                      required
                      className={inviteValid === false ? 'border-destructive' : inviteValid === true ? 'border-success' : ''}
                    />
                    {inviteValid === true && <CheckCircle size={16} weight="fill" className="absolute right-3 top-1/2 -translate-y-1/2 text-success" />}
                    {inviteValid === false && <XCircle size={16} weight="fill" className="absolute right-3 top-1/2 -translate-y-1/2 text-destructive" />}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Ingen invite? <Link to="/" className="text-primary hover:underline">Meld deg på ventelisten</Link>
                  </p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="name">Navn</Label>
                  <Input
                    id="name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Ditt navn"
                    data-testid="register-name-input"
                    required
                  />
                </div>
              </>
            )}
            <div className="space-y-2">
              <Label htmlFor="email">E-post</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="din@epost.no"
                data-testid="login-email-input"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Passord</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={isRegister ? 'Minst 8 tegn' : 'Ditt passord'}
                data-testid="login-password-input"
                required
                minLength={isRegister ? 8 : undefined}
              />
            </div>

            {error && (
              <p className="text-sm text-destructive" data-testid="login-error">{error}</p>
            )}

            <Button
              type="submit"
              className="w-full"
              disabled={loading || (isRegister && inviteValid === false)}
              data-testid="login-submit-button"
            >
              {loading ? (isRegister ? 'Oppretter...' : 'Logger inn...') : (isRegister ? 'Opprett konto' : 'Logg inn')}
            </Button>
          </form>

          <div className="mt-4 pt-4 border-t border-border text-center text-sm space-y-2">
            {!isRegister && (
              <div>
                <Link to="/forgot-password" className="text-primary hover:underline text-xs" data-testid="forgot-password-link">
                  Glemt passord?
                </Link>
              </div>
            )}
            {isRegister ? (
              <span className="text-muted-foreground">
                Har du allerede konto?{' '}
                <button onClick={() => { setIsRegister(false); setError(''); }} className="text-primary hover:underline" data-testid="switch-to-login">
                  Logg inn
                </button>
              </span>
            ) : (
              <span className="text-muted-foreground">
                Har invite-kode?{' '}
                <button onClick={() => { setIsRegister(true); setError(''); }} className="text-primary hover:underline" data-testid="switch-to-register">
                  Registrer deg
                </button>
              </span>
            )}
          </div>
        </div>

        <p className="text-xs text-muted-foreground text-center mt-4">
          Sortilize &mdash; WooCommerce Catalog Management
        </p>
      </div>
    </div>
  );
}
